import {
  briefsForPrompt,
  CRISIS_STOP_MESSAGE,
  drawerKey,
  FAST_MODEL,
  formatWorkOrderBlock,
  frontDesk,
  INVALID_SHEET_MESSAGE,
  JobSheetError,
  needsOutsideInfo,
  PRIVATE_HOLD_MESSAGE,
  receiptLine,
  shelfSensitivity,
  switchboard,
  validateJobSheet,
  type JobSheet,
  type ModelTier,
  type ReceiptPath,
  type Sensitivity,
  type ShelfBrief,
} from "./operatingLoop.js";

export type OperatingContinue = {
  path: "continue";
  model: string;
  overrideModel: boolean;
  briefPrompt: string;
  outsideInfo: boolean;
  jobSheetId: string | null;
  shelves: string[];
  /** Shelf ids used for the answer drawer. Matches the ids the next turn looks up. */
  drawerShelfIds: string[];
  /** null = today's retrieval. An array limits chunks to those shelf ids. */
  shelfIds: string[] | null;
  sensitivity: Sensitivity;
  clientExcerpts: string[];
  question: string;
  tier: ModelTier;
  workOrder: JobSheet;
  workOrderOnly: true;
};

export type OperatingStop = {
  path: "crisis_stop" | "private_hold" | "drawer" | "invalid_sheet";
  message: string;
  receiptLine: string;
};

export type OperatingTurn = OperatingStop | OperatingContinue;

export type OperatingStore = {
  loadBriefs(userId: string, documentIds: string[]): Promise<ShelfBrief[]>;
  searchGeneralBriefs(userId: string, question: string): Promise<ShelfBrief[]>;
  findDrawerAnswer(userId: string, key: string): Promise<string | null>;
  insertJobSheet(userId: string, sheet: JobSheet): Promise<string | null>;
  insertReceipt(input: {
    userId: string;
    path: ReceiptPath;
    model?: string | null;
    totalTokens?: number | null;
    shelves?: string[];
    jobSheetId?: string | null;
    threadId?: string | null;
  }): Promise<void>;
};

export async function prepareOperatingTurn(
  store: OperatingStore,
  input: {
    userId: string;
    question: string;
    selectedDocumentIds: string[];
    autoSelectBest: boolean;
    pickedModel: string;
    privateModel: string | null;
    threadId?: string | null;
    fastMaxChars?: number;
  },
): Promise<OperatingTurn> {
  const question = input.question.trim();
  if (frontDesk({ question, drawerHit: false }) === "crisis_stop") {
    await store.insertReceipt({ userId: input.userId, path: "crisis_stop", threadId: input.threadId, totalTokens: 0 });
    return { path: "crisis_stop", message: CRISIS_STOP_MESSAGE, receiptLine: receiptLine("crisis_stop", null, 0) };
  }

  const selectedBriefs = await store.loadBriefs(input.userId, input.selectedDocumentIds);
  const selectedSensitivity = shelfSensitivity(selectedBriefs);
  const routed = switchboard({
    autoSelectBest: input.autoSelectBest,
    pickedModel: input.pickedModel,
    sensitivity: selectedSensitivity,
    privateModel: input.privateModel,
    question,
    fastMaxChars: input.fastMaxChars,
  });

  if (routed.stop === "private_hold") {
    const shelves = selectedBriefs.map((brief) => brief.documentId);
    await store.insertReceipt({
      userId: input.userId,
      path: "private_hold",
      shelves,
      threadId: input.threadId,
      totalTokens: 0,
    });
    return { path: "private_hold", message: PRIVATE_HOLD_MESSAGE, receiptLine: receiptLine("private_hold", null, 0) };
  }

  const shelfIds = input.selectedDocumentIds;
  const key = drawerKey(question, shelfIds);
  const hit = await store.findDrawerAnswer(input.userId, key);
  if (hit && frontDesk({ question, drawerHit: true }) === "drawer") {
    await store.insertReceipt({ userId: input.userId, path: "drawer", shelves: shelfIds, threadId: input.threadId, totalTokens: 0 });
    return { path: "drawer", message: hit, receiptLine: receiptLine("drawer", null, 0) };
  }

  const matched = selectedBriefs.length > 0 ? selectedBriefs : await store.searchGeneralBriefs(input.userId, question);
  const visible = briefsForPrompt(matched, "general");
  const clientExcerpts = matched
    .filter((brief) => brief.sensitivity !== "general")
    .map((brief) => brief.summary)
    .filter((summary) => summary.length >= 40);

  let sheet: JobSheet;
  try {
    sheet = validateJobSheet({
      question,
      shelves: visible.map((brief) => brief.documentId),
      outsideInfo: needsOutsideInfo(question),
      sensitivity: "general",
      tier: routed.tier === "private" ? "strong" : routed.tier,
      goal: question,
      key_facts: visible.map((brief) => brief.summary).filter(Boolean).slice(0, 8),
      sources: visible.map((brief) => ({ title: brief.whatItIs || brief.documentId, ref: brief.documentId })),
      constraints: ["Answer from this work order only."],
      acceptance_criteria: ["The answer addresses the request and does not invent client names."],
    });
  } catch (error) {
    if (error instanceof JobSheetError) {
      return { path: "invalid_sheet", message: INVALID_SHEET_MESSAGE, receiptLine: "Stopped · invalid job sheet · no model" };
    }
    throw error;
  }

  const jobSheetId = await store.insertJobSheet(input.userId, sheet);
  const named = visible.map((brief) => brief.documentId);
  const shelfScope = input.selectedDocumentIds.length > 0 ? input.selectedDocumentIds : named;

  return {
    path: "continue",
    model: routed.model || input.pickedModel || FAST_MODEL,
    overrideModel: input.autoSelectBest || selectedSensitivity !== "general",
    briefPrompt: formatWorkOrderBlock(sheet),
    outsideInfo: sheet.outsideInfo,
    jobSheetId,
    shelves: sheet.shelves,
    drawerShelfIds: input.selectedDocumentIds,
    shelfIds: shelfScope,
    sensitivity: "general",
    clientExcerpts,
    question,
    tier: sheet.tier,
    workOrder: sheet,
    workOrderOnly: true,
  };
}
